import { FlexBox, Icon, SelectionList } from '@dixa/lib-shared-habla';
import { ReactNode } from 'react';

import { KeyboardKeyCombinations } from 'components/keyboard';
import { CommandPaletteAction } from 'utils/commandPalette';
import { KeyboardKeyCombination } from 'utils/keyboard';

import styles from '../commandPalette.module.scss';

/** Keybindings map type from useCommands */
export type KeybindingsMap = Record<string, readonly KeyboardKeyCombination[]>;

/** Groups actions by their section property. */
function groupActionsBySection(
  actions: readonly CommandPaletteAction[],
): Map<string, CommandPaletteAction[]> {
  const sections = new Map<string, CommandPaletteAction[]>();

  for (const action of actions) {
    const sectionName = action.section ?? 'Actions';
    const sectionActions = sections.get(sectionName) ?? [];
    sections.set(sectionName, [...sectionActions, action]);
  }

  return sections;
}

/**
 * Gets the selection list items for the standard command palette actions,
 * grouped by section.
 */
export function getCommandPaletteActionItems(
  actions: readonly CommandPaletteAction[],
  keybindings?: KeybindingsMap,
): JSX.Element[] {
  const sections = groupActionsBySection(actions);

  return Array.from(sections.entries()).map(([sectionName, sectionActions]) => (
    <SelectionList.Section key={sectionName} title={sectionName}>
      {sectionActions.map((action) =>
        getCommandPaletteActionItemOrList(action, keybindings),
      )}
    </SelectionList.Section>
  ));
}

function getCommandPaletteActionItemOrList(
  action: CommandPaletteAction,
  keybindings?: KeybindingsMap,
): JSX.Element {
  return action.actions
    ? getCommandPaletteActionList(action, keybindings)
    : getCommandPaletteActionItem(action, keybindings);
}

export function getCommandPaletteActionList(
  action: CommandPaletteAction,
  keybindings?: KeybindingsMap,
): JSX.Element {
  return (
    <SelectionList.List
      key={action.id}
      parentItem={getCommandPaletteActionItem(action, keybindings)}
    >
      {action.actions?.map((a) =>
        getCommandPaletteActionItemOrList(a, keybindings),
      )}
    </SelectionList.List>
  );
}

/** Gets the shortcut display for an action */
function getShortcutDisplay(
  action: CommandPaletteAction,
  keybindings?: KeybindingsMap,
): ReactNode {
  if (!action.commandName || !keybindings) return null;

  const actionKeybindings = keybindings[action.commandName];
  if (!actionKeybindings?.length) return null;

  // Use the existing KeyboardKeyCombinations component with simple variant
  return (
    <KeyboardKeyCombinations
      keyCombinations={actionKeybindings}
      variant="simple"
    />
  );
}

export function getCommandPaletteActionItem(
  action: CommandPaletteAction,
  keybindings?: KeybindingsMap,
): JSX.Element {
  const shortcut = getShortcutDisplay(action, keybindings);
  const hasIconOrPrefix = action.icon || action.prefix;

  return (
    <SelectionList.Item
      key={action.id}
      value={action}
      noFixedSelectionStateSpace
      className={styles.item}
    >
      <FlexBox
        alignItems="center"
        justifyContent="space-between"
        className={styles.actionItem}
      >
        <FlexBox alignItems="center" gap="300">
          {hasIconOrPrefix && (
            <span className={styles.actionItemIcon}>
              {action.icon && (
                <Icon name={action.icon} size={16} color={action.iconColor} />
              )}
              {action.prefix}
            </span>
          )}
          <span>
            {action.label}
            {action.suffix}
          </span>
        </FlexBox>
        {shortcut}
      </FlexBox>
    </SelectionList.Item>
  );
}
